@if (ActionError) {
<div class="action-error" role="alert">{{ ActionError }} @if (!OrderDetails && !Loading) { <button (click)="loadAll()">Retry</button> }</div>
}
@if (Loading) {
<div class="loading">Loading...</div>
} @else if (OrderDetails) {

<div class="detail-header">
  <h3>Order #{{ OrderID }} · Shipping</h3>
  <span class="status-badge">{{ OrderDetails.StatusName }}</span>
  <button class="btn-bg-red" (click)="returnToList()">← Return</button>
</div>

<rms-tab-group>

  <!-- Order Details tab: matches wireframe — fields left, Actions sidebar right -->
  <rms-tab label="Order Details">
    <ng-template>
      <div class="outbound-details-layout">

        <div class="details-panel">
          <div class="outbound-details-grid">
            @if (lineWarnings.length) {
            <div class="field-group span-3 action-error" role="alert">
              @for (warning of lineWarnings; track warning) { <div>{{ warning }}</div> }
            </div>
            }
            <div class="field-group">
              <label>Warehouse</label>
              <div>{{ OrderDetails.Warehouse }}</div>
            </div>
            <div class="field-group">
              <label>Carrier / Trucker</label>
              <div>{{ OrderDetails.TruckerName }}</div>
            </div>
            <div class="field-group">
              <label>Pickup Date</label>
              <rms-date [ngModel]="PickupDate" [blockedDates]="BlockedDates" (ngModelChange)="savePickupDate($event)"></rms-date>
            </div>
            <div class="field-group">
              <label>Freight Rate</label>
              <input aria-label="Freight Rate" maxlength="20" [(ngModel)]="TruckingRate" />
            </div>
            <div class="field-group">
              <label>Quote Number</label>
              <input aria-label="Quote Number" maxlength="30" [(ngModel)]="TruckingQuote" />
            </div>
            <div class="field-group">
              <label>Ship To Address</label>
              @if (ShippingAddresses.length === 0) {
              <p class="muted">No shipping addresses on this order.</p>
              } @else {
              <select [ngModel]="SelectedShipToAddressID" (ngModelChange)="saveShipToAddress($event)" [disabled]="SavingShipTo">
                @for (addr of ShippingAddresses; track addr.OrderShippingAddressID) {
                <option [value]="addr.OrderShippingAddressID">
                  {{ addr.Street }}, {{ addr.City }}, {{ addr.State }} {{ addr.ZipCode }}{{ addr.IsPrimary ? ' (Primary)' : '' }}
                </option>
                }
              </select>
              }
            </div>
            <div class="field-group instruction-field">
              <label>Warehouse Instructions</label>
              <textarea aria-label="Warehouse Instructions" maxlength="500" [(ngModel)]="WarehouseInstructions"></textarea>
            </div>
            <div class="field-group instruction-field">
              <label>Trucker Instructions</label>
              <textarea aria-label="Trucker Instructions" maxlength="250" [(ngModel)]="BOLInstructions"></textarea>
            </div>
            @if (OrderDetails.HoldReason) {
            <div class="field-group span-3 hold-banner">
              On Hold: {{ OrderDetails.HoldReason }}
            </div>
            }
            <!-- 5.2.9: only visible to Executive / System Admin AD group
                 members — canOverrideZeroPrice checks real Azure AD group
                 membership via AuthService.isInAnyGroup(), same pattern
                 already used to gate editing in Customer Management. -->
            @if (canOverrideZeroPrice) {
            <div class="field-group span-3">
              <label>
                <input type="checkbox" [checked]="OrderDetails.ZeroPriceOverride" (change)="toggleZeroPriceOverride()" />
                Authorize zero-price shipment (Executive / System Admin only)
              </label>
            </div>
            }
          </div>

          <div class="save-row">
            <button class="btn-bg-black" (click)="saveFreight()" [disabled]="busy">{{ Saving ? 'Saving…' : 'Save Freight Details' }}</button>
            @if (freightChanged) { <span class="muted">Unsaved freight changes</span> }
          </div>
        </div>

        <!-- Actions sidebar, matching the wireframe -->
        <div class="actions-sidebar">
          <h4>Actions</h4>
          <p class="muted">Select orders in the shipping queue to create a warehouse batch.</p>
          <button mat-flat-button class="btn-bg-blue" [disabled]="busy || freightChanged" (click)="viewDocument('Delivery Order')">{{Generating ? 'Preparing document…' : 'View delivery order'}}</button>
          <button mat-stroked-button [disabled]="busy || freightChanged" (click)="viewDocument('Bill of Lading')">View bill of lading</button>
          <p class="muted">Documents are prepared automatically when opened.</p>
          @if (OrderDetails.HoldReason) {
          <button class="action-btn action-btn-success" [disabled]="busy || freightChanged" (click)="releaseHold()">Release Hold</button>
          } @else {
          <button class="action-btn action-btn-warning" [disabled]="busy || freightChanged" (click)="placeOnHold()">Place On Hold</button>
          }
        </div>

      </div>
    </ng-template>
  </rms-tab>

  <!-- Documents tab: generate + preview + send, with inline recipient picker (5.2.18) -->
  <rms-tab label="Documents">
    <ng-template>
      <div class="delivery-notice" role="status">Email test mode: documents go only to m.bray&#64;royalny.com. Warehouse delivery status is unchanged.</div>
      @if (EmailResult) { <p class="delivery-notice" role="status">{{ EmailResult }}</p> }
      <table class="doc-table">
        <thead>
          <tr><th>Document</th><th>Status</th><th>Generated</th><th>Action</th></tr>
        </thead>
        <tbody>
          @for (doc of Documents; track doc.DocumentType) {
          <tr>
            <td>{{ doc.DocumentType }}</td>
            <td>{{ doc.Status }}</td>
            <td>{{ doc.GeneratedDate }}</td>
            <td>
              <button [disabled]="busy || freightChanged" (click)="viewDocument(doc.DocumentType)">View {{doc.DocumentType}}</button>
              @if(doc.FilePath && doc.Status !== 'Pending') {<button [disabled]="busy || freightChanged" (click)="generateDocument(doc.DocumentType)">Refresh saved PDF</button>}
              <button [disabled]="busy || freightChanged" (click)="openSendPicker(doc.DocumentType)">Send test email</button>
            </td>
          </tr>


          }
        </tbody>
      </table>
    </ng-template>
  </rms-tab>

  <!-- Transmission History tab -->
  <rms-tab label="Transmission History">
    <ng-template>
      <table class="doc-table">
        <thead>
          <tr><th>Document</th><th>Sent</th><th>Recipients</th><th>Result</th></tr>
        </thead>
        <tbody>
          @for (t of TransmissionHistory; track t.SentDate) {
          <tr>
            <td>{{ t.DocumentType }}</td>
            <td>{{ t.SentDate }}</td>
            <td>{{ t.RecipientsJson }}</td>
            <td>{{ t.Result }}</td>
          </tr>
          }
        </tbody>
      </table>
    </ng-template>
  </rms-tab>

  <!-- Audit tab -->
  <rms-tab label="Audit">
    <ng-template>
      @if (ActionHistory.length === 0) {
      <p>No history yet.</p>
      }
      @for (entry of ActionHistory; track entry.OrderActionHistoryID) {
      <div class="history-entry">
        <span class="history-action">{{ entry.ActionType }}</span>
        <span class="history-detail">{{ entry.ActionDetail }}</span>
        <span class="history-date">{{ entry.ActionDate }}</span>
      </div>
      }
    </ng-template>
  </rms-tab>

</rms-tab-group>
}
